/**
 * @cfleet/auth - Token utilities
 *
 * Utilities for working with JWT tokens.
 */

/**
 * Decoded JWT payload structure
 */
export interface JwtPayload {
  /** Subject (user ID) */
  sub?: string;
  /** Expiration time (Unix timestamp in seconds) */
  exp?: number;
  /** Issued at time (Unix timestamp in seconds) */
  iat?: number;
  /** Not before time (Unix timestamp in seconds) */
  nbf?: number;
  /** Issuer */
  iss?: string;
  /** Audience */
  aud?: string | string[];
  /** JWT ID */
  jti?: string;
  /** Custom claims */
  [key: string]: unknown;
}

/**
 * Parse a JWT token and extract the payload
 *
 * @param token - The JWT token string
 * @returns The decoded payload or null if invalid
 *
 * @example
 * ```ts
 * const payload = parseJwt(token);
 * if (payload) {
 *   console.log('User ID:', payload.sub);
 *   console.log('Expires:', new Date(payload.exp! * 1000));
 * }
 * ```
 */
export function parseJwt(token: string): JwtPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const payload = parts[1];
    // Handle URL-safe base64 encoding
    let base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padding = base64.length % 4;
    if (padding > 0) {
      base64 += '='.repeat(4 - padding);
    }

    const decoded =
      typeof atob !== 'undefined' ? atob(base64) : Buffer.from(base64, 'base64').toString('binary');

    const jsonPayload = decodeURIComponent(
      decoded
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

/**
 * Check if a JWT token is expired
 *
 * @param token - The JWT token string
 * @param bufferSeconds - Buffer time in seconds before actual expiration (default: 30)
 * @returns true if the token is expired or will expire within the buffer time
 *
 * @example
 * ```ts
 * if (isTokenExpired(accessToken)) {
 *   await refreshToken();
 * }
 *
 * // Check with 60 second buffer
 * if (isTokenExpired(accessToken, 60)) {
 *   await refreshToken();
 * }
 * ```
 */
export function isTokenExpired(token: string, bufferSeconds: number = 30): boolean {
  const payload = parseJwt(token);
  if (!payload || typeof payload.exp !== 'number') {
    // If we can't parse or no expiration, assume expired for safety
    return true;
  }

  const now = Math.floor(Date.now() / 1000);
  return payload.exp <= now + bufferSeconds;
}

/**
 * Get the expiration timestamp from a JWT token
 *
 * @param token - The JWT token string
 * @returns The expiration timestamp in milliseconds, or null if invalid/no expiration
 *
 * @example
 * ```ts
 * const expiresAt = getTokenExpiration(accessToken);
 * if (expiresAt) {
 *   const expiresIn = expiresAt - Date.now();
 *   console.log(`Token expires in ${expiresIn / 1000} seconds`);
 * }
 * ```
 */
export function getTokenExpiration(token: string): number | null {
  const payload = parseJwt(token);
  if (!payload || typeof payload.exp !== 'number') {
    return null;
  }
  return payload.exp * 1000; // Convert to milliseconds
}

/**
 * Get the time remaining until token expiration
 *
 * @param token - The JWT token string
 * @returns Time remaining in milliseconds, or 0 if expired/invalid
 */
export function getTokenTimeRemaining(token: string): number {
  const expiresAt = getTokenExpiration(token);
  if (!expiresAt) {
    return 0;
  }
  return Math.max(0, expiresAt - Date.now());
}

/**
 * Extract the user ID (subject) from a JWT token
 *
 * @param token - The JWT token string
 * @returns The user ID or null if not found
 */
export function getUserIdFromToken(token: string): string | null {
  const payload = parseJwt(token);
  return payload?.sub ?? null;
}

/**
 * Calculate when to refresh a token based on expiration time
 *
 * @param token - The JWT token string
 * @param refreshThresholdMs - How many ms before expiration to trigger refresh (default: 60000 = 1 minute)
 * @returns Timestamp in ms when token should be refreshed, or null if invalid
 */
export function calculateRefreshTime(
  token: string,
  refreshThresholdMs: number = 60000
): number | null {
  const expiresAt = getTokenExpiration(token);
  if (!expiresAt) {
    return null;
  }
  return Math.max(Date.now(), expiresAt - refreshThresholdMs);
}

/**
 * Check if a JWT token is expired (zero-buffer convenience wrapper)
 *
 * @param token - JWT token string
 * @returns true if token is expired or malformed, false if valid
 */
export function isJwtExpired(token: string): boolean {
  return isTokenExpired(token, 0);
}

/**
 * Check if a JWT token is valid (not expired)
 *
 * @param token - JWT token string
 * @returns true if token is valid, false if expired or malformed
 */
export function isJwtValid(token: string): boolean {
  return !isTokenExpired(token, 0);
}
